// Librairies
import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { MapThemeDirective } from 'src/app/directives/map-theme.directive';
import { TranslateModule } from '@ngx-translate/core';

// Services
import { SynthesisStore } from 'src/app/services/stores/synthesis.store';

// Interfaces
import { TenantProject } from 'src/app/interfaces/api/company';

// Components
import { ChipTone, chipTone } from 'src/app/components/project-pages/dashboard/widgets/table/table.component';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { TooltipComponent } from 'projects/ui-library/src/lib/components/tooltip/tooltip.component';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';

// Settings, in widget.design.projectMap.
export interface ProjectMapDesign {
  warning?: number | null;
  critical?: number | null;
  higherIsWorse?: boolean;          // default true
  maxRadius?: number | null;        // px, the biggest bubble
  legend?: boolean;                 // the colours and the size, under the map
}

export interface ProjectMapLegendItem {
  tone: ChipTone;
  bound: string;                    // the threshold, e.g. "≥ 120"
}

export interface ProjectBubble {
  pid: string;
  name: string;
  city: string;
  value: number;
  lat: number;
  lng: number;
  radius: number;                   // px
  tone: ChipTone;
}

export const DEFAULT_MAX_RADIUS = 28;
const MIN_RADIUS = 4;

export const BUBBLE_COLORS: Record<ChipTone, string> = {
  good: '#00b09b',
  warning: '#f39c12',
  critical: '#ff6b6b',
  neutral: '',                      // the theme color
};

// One bubble per project row ({ y, p, pid }) placed on its tenant project; its area follows the value.
export function projectBubbles(rows: any[], projects: TenantProject[], design: ProjectMapDesign): { bubbles: ProjectBubble[]; missing: string[] } {
  const byId = new Map(projects.map(project => [project.id, project]));
  const maxRadius = design.maxRadius && design.maxRadius > 0 ? design.maxRadius : DEFAULT_MAX_RADIUS;
  const values = (rows ?? []).map(row => Math.max(0, Number(row.y) || 0));
  const max = Math.max(0, ...values);
  const bubbles: ProjectBubble[] = [];
  const missing: string[] = [];

  (rows ?? []).forEach((row, index) => {
    const project = byId.get(row.pid);
    const lat = Number(project?.lat);
    const lng = Number(project?.lon);
    const name = row.p ?? project?.name ?? '';

    if (!project || project.lat == null || project.lon == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      missing.push(name);
      return;
    }

    const value = values[index]!;
    bubbles.push({
      pid: row.pid,
      name,
      city: project.city ?? '',
      value,
      lat,
      lng,
      radius: max > 0 ? Math.max(MIN_RADIUS, maxRadius * Math.sqrt(value / max)) : MIN_RADIUS,
      tone: chipTone(value, { format: 'chip', warning: design.warning ?? undefined, critical: design.critical ?? undefined, higherIsWorse: design.higherIsWorse }),
    });
  });

  return { bubbles, missing };
}

// The colours of the thresholds, the worst first; none without both thresholds (one colour only).
export function projectMapLegend(design: ProjectMapDesign): ProjectMapLegendItem[] {
  if (design.warning == null || design.critical == null) return [];

  const worse = design.higherIsWorse !== false;
  const format = (value: number) => value.toLocaleString();
  return [
    { tone: 'critical', bound: `${worse ? '≥' : '≤'} ${format(design.critical)}` },
    { tone: 'warning', bound: `${worse ? '≥' : '≤'} ${format(design.warning)}` },
    { tone: 'good', bound: `${worse ? '<' : '>'} ${format(design.warning)}` },
  ];
}

// Synthesis only: the tenant projects on a map, one bubble each, sized and colored by the value.
@Component({
  selector: 'app-project-map',
  standalone: true,
  imports: [
    WidgetLoadingComponent,
    TooltipComponent,
    WidgetTitleComponent,
    CommonModule,
    GoogleMapsModule,
    MapThemeDirective,
    TranslateModule
  ],
  templateUrl: './project-map.component.html',
  styleUrl: './project-map.component.scss'
})

export class ProjectMapComponent extends WidgetDirective {
  // Services
  private readonly synthesisStore: SynthesisStore = inject(SynthesisStore);

  // Component API
  @ViewChild(GoogleMap) map?: GoogleMap;

  // Variables
  bubbles: ProjectBubble[] = [];
  missing: string[] = [];
  markerOptions: Record<string, google.maps.MarkerOptions> = {};
  // The hovered bubble and the mouse position (viewport), for the tooltip.
  hovered: { bubble: ProjectBubble; x: number; y: number } | null = null;
  showLegend: boolean = false;
  legend: ProjectMapLegendItem[] = [];
  readonly colors: Record<ChipTone, string> = BUBBLE_COLORS;
  titleStyle: string = '';
  readonly mapOptions: google.maps.MapOptions = { center: { lat: 46.6, lng: 2.4 }, zoom: 5, streetViewControl: false, mapTypeControl: false };

  // The positions come with the tenant projects: the company list is not expanded for CAD.42 (every project).
  tenantProjects(): TenantProject[] {
    return this.synthesisStore.tenant()?.projects ?? [];
  }

  init(): void {
    if (!this.widget || !this.widget.visible || this.resize) return;

    const design = this.widget.design ?? {};
    const result = projectBubbles(this.widget.data ?? [], this.tenantProjects(), design.projectMap ?? {});
    const theme = getComputedStyle(document.body).getPropertyValue('--primary-color').trim() || '#1F77B4';

    this.showTitle = design.title?.display ?? true;
    this.titleStyle = `font-size:${design.title?.fontSize || 16}px;text-align:${design.title?.align || 'center'};`;
    this.bubbles = result.bubbles;
    this.missing = result.missing;
    this.showLegend = !!design.projectMap?.legend;
    this.legend = projectMapLegend(design.projectMap ?? {});

    this.markerOptions = Object.fromEntries(this.bubbles.map(bubble => [bubble.pid, {
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: bubble.radius,
        fillColor: BUBBLE_COLORS[bubble.tone] || theme,
        fillOpacity: 0.7,
        strokeColor: '#ffffff',
        strokeWeight: 1,
      },
    }]));

    this.widgetRendered = true;
    this.cdr.detectChanges();
    this.fit();
  }

  // Frames every bubble; a single one is centered.
  fit(): void {
    if (!this.map || !this.bubbles.length) return;

    if (this.bubbles.length === 1) {
      this.map.googleMap?.setCenter({ lat: this.bubbles[0]!.lat, lng: this.bubbles[0]!.lng });
      this.map.googleMap?.setZoom(10);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    this.bubbles.forEach(bubble => bounds.extend({ lat: bubble.lat, lng: bubble.lng }));
    this.map.fitBounds(bounds, 40);
  }

  hover(bubble: ProjectBubble, event: google.maps.MapMouseEvent): void {
    const mouse = event.domEvent as MouseEvent;
    if (mouse?.clientX === undefined) return;

    this.hovered = { bubble, x: mouse.clientX, y: mouse.clientY };
    this.cdr.detectChanges();
  }

  // Nothing around the map: no frame, the map fills the widget.
  get bare(): boolean {
    return !this.showTitle && !this.showLegend && !this.missing.length;
  }
}
